/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import {
	LICENSE_KEY,
	AI_API_KEY,
	UPLOADCARE_PUBKEY,
	DOCUMENT_ID,
	CLOUD_SERVICES_TOKEN_URL,
	CLOUD_SERVICES_WEBSOCKET_URL
} from '../../constants/configPlaceholders';
import { getLicenseKeyFromUserData } from '../getLicenseKeyFromUserData/getLicenseKeyFromUserData';
import { getWebsocketUrlsFromUserData } from '../getWebsocketUrlsFromUserData/getWebsocketUrlsFromUserData';
import type { CustomerPortalAccount } from '../../contexts/CustomerPortalContext';
import type { InstallMethod } from '../../constants/installation';
import { getVariableName } from '../getVariableName/getVariableName';
import type { EditorPlugins } from '../../types/EditorPlugins';
import { getPluginByClassName } from '../getPluginByClassName/getPluginByClassName';
import { getBlockComment } from '../getBlockComment/getBlockComment';

type getTokenVariablesArg = {
	editorConfig: string;
	editorPlugins: EditorPlugins;
	customerPortalAccount: CustomerPortalAccount | null;
	evaluationLicense: string;
	selectedInstallMethod: InstallMethod;
};

export function getTokenVariables( getTokenVariablesArg: getTokenVariablesArg ): string {
	return [
		getLicenseKeyDeclaration( getTokenVariablesArg ),
		getAiApiKeyDeclaration( getTokenVariablesArg ),
		getUploadcarePubkeyDeclaration( getTokenVariablesArg ),
		getDocumentIdDeclaration( getTokenVariablesArg ),
		getCloudServicesDeclarations( getTokenVariablesArg )
	].map( str => str.trim() ).filter( Boolean ).join( '\n\n' );
}

function getLicenseKeyDeclaration( {
	editorPlugins,
	customerPortalAccount,
	evaluationLicense,
	selectedInstallMethod
}: getTokenVariablesArg ): string {
	const evalComment = getBlockComment( [
		'This is a 24-hour evaluation key. Create a free account to use CDN: https://portal.ckeditor.com/checkout?plan=free'
	] );
	const trialComment = getBlockComment( [
		'Create a free account with a trial: https://portal.ckeditor.com/checkout?plan=free'
	] );
	const missingLicenseComment = getBlockComment( [
		'Please update your license key.',
		'See: https://ckeditor.com/docs/ckeditor5/latest/getting-started/setup/license-key-and-activation.html'
	] );

	if ( customerPortalAccount ) {
		const licenseKey = getLicenseKeyFromUserData( customerPortalAccount )!;

		if ( licenseKey ) {
			return getConstDeclaration( getVariableName( { LICENSE_KEY } ), licenseKey );
		}

		return [
			missingLicenseComment,
			getConstDeclaration( getVariableName( { LICENSE_KEY } ), LICENSE_KEY )
		].join( '\n' );
	}

	const hasPremiumPlugins = editorPlugins
		.map( getPluginByClassName )
		.some( plugin => plugin.premium && plugin.premium.includes( 'licenseKey' ) );

	if ( hasPremiumPlugins ) {
		return [
			trialComment,
			getConstDeclaration( getVariableName( { LICENSE_KEY } ), LICENSE_KEY )
		].join( '\n' );
	}

	if ( selectedInstallMethod === 'cdn' ) {
		return [
			evalComment,
			getConstDeclaration( getVariableName( { LICENSE_KEY } ), evaluationLicense )
		].join( '\n' );
	}

	return [
		trialComment,
		getConstDeclaration( getVariableName( { LICENSE_KEY } ), 'GPL' ) + ` // or ${ LICENSE_KEY }.`
	].join( '\n' );
}

function getAiApiKeyDeclaration( { editorConfig }: getTokenVariablesArg ): string {
	if ( !editorConfig.includes( getVariableName( { AI_API_KEY } ) ) ) {
		return '';
	}

	return [
		getBlockComment( [
			'USE THIS INTEGRATION METHOD ONLY FOR DEVELOPMENT PURPOSES.',
			'',
			'This sample is configured to use OpenAI API for handling AI Assistant queries.',
			'See: https://ckeditor.com/docs/ckeditor5/latest/features/ai-assistant/ai-assistant-integration.html',
			'for a full integration and customization guide.'
		] ),
		getConstDeclaration( getVariableName( { AI_API_KEY } ), AI_API_KEY )
	].join( '\n' );
}

function getUploadcarePubkeyDeclaration( { editorConfig }: getTokenVariablesArg ): string {
	if ( !editorConfig.includes( getVariableName( { UPLOADCARE_PUBKEY } ) ) ) {
		return '';
	}

	return [
		getBlockComment( [
			'Create a free account to test Uploadcare with a trial: https://app.uploadcare.com/accounts/signup/',
			'',
			'If you are interested in using Uploadcare on production, contact us: https://ckeditor.com/contact-sales/'
		] ),
		getConstDeclaration( getVariableName( { UPLOADCARE_PUBKEY } ), UPLOADCARE_PUBKEY )
	].join( '\n' );
}

function getDocumentIdDeclaration( { editorConfig }: getTokenVariablesArg ): string {
	if ( !editorConfig.includes( getVariableName( { DOCUMENT_ID } ) ) ) {
		return '';
	}

	return [
		getBlockComment( [
			'Unique ID that will be used to identify this document. E.g. you may use ID taken from your database.',
			'Read more: https://ckeditor.com/docs/ckeditor5/latest/api/module_collaboration-core_config-RealTimeCollaborationConfig.html'
		] ),
		getConstDeclaration( getVariableName( { DOCUMENT_ID } ), DOCUMENT_ID )
	].join( '\n' );
}

function getCloudServicesDeclarations( { editorConfig, customerPortalAccount }: getTokenVariablesArg ): string {
	let requiresComment = false;
	const declarations: Array<string> = [];

	const { tokenUrl, webSocketUrl } = getWebsocketUrlsFromUserData( customerPortalAccount );

	if ( editorConfig.includes( getVariableName( { CLOUD_SERVICES_TOKEN_URL } ) ) ) {
		declarations.push( getConstDeclaration(
			getVariableName( { CLOUD_SERVICES_TOKEN_URL } ),
			tokenUrl || CLOUD_SERVICES_TOKEN_URL
		) );

		if ( !tokenUrl ) {
			requiresComment = true;
		}
	}

	if ( editorConfig.includes( getVariableName( { CLOUD_SERVICES_WEBSOCKET_URL } ) ) ) {
		declarations.push( getConstDeclaration(
			getVariableName( { CLOUD_SERVICES_WEBSOCKET_URL } ),
			webSocketUrl || CLOUD_SERVICES_WEBSOCKET_URL
		) );

		if ( !webSocketUrl ) {
			requiresComment = true;
		}
	}

	if ( !declarations.length ) {
		return '';
	}

	if ( requiresComment ) {
		declarations.unshift( getBlockComment( [
			'Please update the following values with your tokens.',
			'Instructions on how to obtain them: https://ckeditor.com/docs/trial/latest/guides/real-time/quick-start.html'
		] ) );
	}

	return declarations.join( '\n' );
}

function getConstDeclaration( name: string, value: string ): string {
	return `const ${ name } = '${ value }';`;
}
